import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPut } from '../lib/api.js';

export type TenantSummary = {
  id: string;
  name: string;
  slug: string;
  default_master_visibility: boolean;
};

export type SharingCourse = {
  id: string;
  name: string;
  cricos_code: string | null;
  curation_state: string;
  rule: boolean | null;
  effective_visible: boolean;
};

export type SharingProvider = {
  id: string;
  name: string;
  short_name: string | null;
  rule: boolean | null;
  effective_visible: boolean;
  courses: SharingCourse[];
};

export type TenantSharing = {
  tenant: { id: string; name: string; default_master_visibility: boolean };
  providers: SharingProvider[];
};

export function useTenantsList() {
  return useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: () => apiGet<{ tenants: TenantSummary[] }>('/admin/tenants'),
    staleTime: 30_000,
  });
}

export function useTenantSharing(tenantId: string) {
  return useQuery({
    queryKey: ['admin', 'sharing', tenantId],
    queryFn: () => apiGet<TenantSharing>(`/admin/tenants/${tenantId}/sharing`),
    staleTime: 10_000,
  });
}

export function useSetCourseVisibility(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ courseId, visible }: { courseId: string; visible: boolean }) =>
      apiPut<TenantSharing>(`/admin/tenants/${tenantId}/sharing/courses/${courseId}`, { visible }),
    onSuccess: (data) => qc.setQueryData(['admin', 'sharing', tenantId], data),
  });
}

export function useSetProviderVisibility(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ providerId, visible }: { providerId: string; visible: boolean }) =>
      apiPut<TenantSharing>(
        `/admin/tenants/${tenantId}/sharing/providers/${providerId}`,
        { visible },
      ),
    onSuccess: (data) => qc.setQueryData(['admin', 'sharing', tenantId], data),
  });
}
